/*
 * Display-Inhalt: laedt beim Klick auf eine Artikelkarte den Technikdaten-Block
 * des jeweiligen Artikels nach.
 *
 * Nutzt das Alt-Template-Muster des Themes (?view=tech-details), nicht die
 * Section Rendering API: ?section_id= wuerde im Kontext des Display-Produkts
 * rendern und nicht im Kontext des angeklickten Einzelartikels.
 *
 * Siehe templates/product.tech-details.liquid und snippets/display-detail.liquid.
 */

if (!customElements.get('display-inhalt')) {
  class DisplayInhalt extends HTMLElement {
    constructor() {
      super();
      this.cache = new Map();
      this.controller = null;
      this.onCardClick = this.onCardClick.bind(this);
    }

    connectedCallback() {
      this.panel = document.getElementById(this.dataset.panel);
      this.cards = Array.from(this.querySelectorAll('[data-display-card]'));

      const active = this.cards.find((card) => card.getAttribute('aria-expanded') === 'true');
      if (active && this.panel) {
        if (this.panel.innerHTML.trim() === '') {
          // Serverseitiges Rendering hat nichts geliefert - nachladen statt
          // Leere cachen, sonst reagiert die erste Karte nie auf Klicks.
          this.render(active.dataset.productUrl);
        } else {
          this.cache.set(active.dataset.productUrl, this.panel.innerHTML);
        }
      }

      this.addEventListener('click', this.onCardClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onCardClick);
      if (this.controller) this.controller.abort();
    }

    onCardClick(event) {
      const card = event.target.closest('[data-display-card]');
      if (!card || !this.contains(card) || !this.panel) return;

      event.preventDefault();
      if (card.getAttribute('aria-expanded') === 'true') return;

      this.cards.forEach((item) => item.setAttribute('aria-expanded', 'false'));
      card.setAttribute('aria-expanded', 'true');

      this.render(card.dataset.productUrl);
    }

    async render(productUrl) {
      if (!productUrl) return;

      if (this.cache.has(productUrl)) {
        this.setPanel(this.cache.get(productUrl));
        return;
      }

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      this.panel.dataset.loading = 'true';

      try {
        const separator = productUrl.includes('?') ? '&' : '?';
        const response = await fetch(`${productUrl}${separator}view=tech-details`, {
          signal: this.controller.signal,
        });

        if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);

        const text = await response.text();
        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const detail = parsed.querySelector('.display-detail');
        const html = detail ? detail.outerHTML : text;

        this.cache.set(productUrl, html);
        this.setPanel(html);
      } catch (error) {
        if (error.name === 'AbortError') return;

        console.error('[display-inhalt]', error);
        this.setPanel(
          '<p class="text-sm text-opacity">Die Details konnten nicht geladen werden. Bitte die Produktseite direkt öffnen.</p>'
        );
      } finally {
        this.panel.dataset.loading = 'false';
        this.controller = null;
      }
    }

    setPanel(html) {
      this.panel.innerHTML = html;
      // Lazy-Images und Hover-Buttons des Themes im nachgeladenen Markup aktivieren.
      this.panel.querySelectorAll('img[loading="lazy"]').forEach((image) => {
        if (image.complete) image.classList.add('loaded');
      });
    }
  }

  customElements.define('display-inhalt', DisplayInhalt);
}
